import { ChangeDetectorRef, Component, DestroyRef, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ToastService } from 'src/app/services/toast.service';
import { SocialFeedService } from '../social-feed/services/social-feed.service';
import { CommunityPrivacy, MyCommunityInvite, SocialCommunity } from '../social-feed/models/social-feed.model';
import { communityError } from '../community/community-utils';
import { SocialHubService } from '../social-feed/services/social-hub.service';

type DirectoryTab = 'discover' | 'mine' | 'invites';

/**
 * The Communities directory: discover (every community, biggest first),
 * yours, and invites you've received. The tab, search and filter are kept
 * in the address so a notification can open the Invites tab directly.
 */
@Component({
  selector: 'app-communities',
  templateUrl: './communities.component.html',
  styleUrls: ['./communities.component.scss'],
  standalone: false,
})
export class CommunitiesComponent implements OnInit {
  tab: DirectoryTab = 'discover';
  query = '';
  privacy: CommunityPrivacy | '' = '';
  private query$ = new Subject<string>();

  communities: SocialCommunity[] = [];
  loading = true;
  loadError = false;
  page = 1;
  hasMore = false;
  loadingMore = false;

  invites: MyCommunityInvite[] = [];
  invitesLoading = false;
  inviteCount = 0;
  answering = new Set<number>();
  /** Communities with a join / request in flight. */
  busy = new Set<number>();

  readonly skeletons = [1, 2, 3, 4];

  constructor(
    private social: SocialFeedService,
    private route: ActivatedRoute,
    private router: Router,
    private toaster: ToastService,
    private hub: SocialHubService,
    private cdr: ChangeDetectorRef,
    private destroyRef: DestroyRef
  ) {}

  ngOnInit(): void {
    const params = this.route.snapshot.queryParamMap;
    const tab = params.get('tab') as DirectoryTab | null;
    this.tab = tab === 'mine' || tab === 'invites' ? tab : 'discover';
    this.query = params.get('q') || '';
    const privacy = params.get('privacy');
    this.privacy = privacy === 'public' || privacy === 'private' ? privacy : '';

    this.query$.pipe(debounceTime(300), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.syncUrl();
      this.load();
    });
    this.loadInviteCount();
    this.tab === 'invites' ? this.loadInvites() : this.load();
  }

  setTab(tab: DirectoryTab): void {
    if (this.tab === tab) return;
    this.tab = tab;
    this.syncUrl();
    tab === 'invites' ? this.loadInvites() : this.load();
  }

  setPrivacy(privacy: CommunityPrivacy | ''): void {
    this.privacy = privacy;
    this.syncUrl();
    this.load();
  }

  onQuery(value: string): void {
    this.query$.next(value.trim());
  }

  private syncUrl(): void {
    this.router.navigate([], {
      queryParams: { tab: this.tab === 'discover' ? null : this.tab, q: this.query.trim() || null, privacy: this.privacy || null },
      replaceUrl: true,
    });
  }

  load(more = false): void {
    const page = more ? this.page + 1 : 1;
    if (more) {
      this.loadingMore = true;
    } else {
      this.loading = true;
      this.loadError = false;
    }
    this.social
      .getCommunities({
        scope: this.tab === 'mine' ? 'mine' : 'all',
        q: this.query.trim() || undefined,
        privacy: this.privacy || undefined,
        page,
      })
      .subscribe({
        next: (res) => {
          const have = new Set(more ? this.communities.map((c) => c.id) : []);
          this.communities = more ? [...this.communities, ...res.results.filter((c) => !have.has(c.id))] : res.results;
          this.page = page;
          this.hasMore = !!res.next;
          this.loading = this.loadingMore = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.loading = this.loadingMore = false;
          this.loadError = !more;
          this.cdr.detectChanges();
        },
      });
  }

  loadInviteCount(): void {
    this.social.getMyCommunityInvites().subscribe({
      next: (res) => {
        this.inviteCount = res.count;
        this.cdr.detectChanges();
      },
      error: () => undefined,
    });
  }

  loadInvites(): void {
    this.invitesLoading = true;
    this.social.getMyCommunityInvites().subscribe({
      next: (res) => {
        this.invites = res.results;
        this.inviteCount = res.count;
        this.invitesLoading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.invitesLoading = false;
        this.cdr.detectChanges();
      },
    });
  }

  answer(invite: MyCommunityInvite, accept: boolean): void {
    if (this.answering.has(invite.id)) return;
    this.answering.add(invite.id);
    this.social.answerCommunityInvite(invite.id, accept).subscribe({
      next: (community) => {
        this.answering.delete(invite.id);
        this.invites = this.invites.filter((i) => i.id !== invite.id);
        this.inviteCount = Math.max(0, this.inviteCount - 1);
        this.hub.refreshCommunities();
        if (accept) {
          this.toaster.showSuccess(`Welcome to ${community.name}!`);
          this.router.navigate(['/communities', community.slug]);
        }
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.answering.delete(invite.id);
        this.toaster.showNote(communityError(err, "Couldn't answer the invite."), 'danger');
        this.loadInvites();
      },
    });
  }

  /** The card's main button: join a public one, ask to join a private one. */
  quickJoin(community: SocialCommunity, event: Event): void {
    event.stopPropagation();
    if (this.busy.has(community.id)) return;
    if (community.privacy === 'private' && !community.my_invite_id) {
      // A note for the moderators is offered on the community's own page.
      this.router.navigate(['/communities', community.slug]);
      return;
    }
    this.busy.add(community.id);
    const call = community.my_invite_id
      ? this.social.answerCommunityInvite(community.my_invite_id, true)
      : this.social.joinCommunity(community.slug);
    call.subscribe({
      next: (updated) => {
        this.busy.delete(community.id);
        Object.assign(community, updated);
        this.hub.refreshCommunities();
        this.toaster.showSuccess(`Welcome to ${updated.name}!`);
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.busy.delete(community.id);
        this.toaster.showNote(communityError(err, "Couldn't join. Please try again."), 'danger');
        this.cdr.detectChanges();
      },
    });
  }

  open(slug: string): void {
    this.router.navigate(['/communities', slug]);
  }

  initials(name: string): string {
    return (name || '')
      .split(/\s+/)
      .map((w) => w[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase();
  }

  imageUrl(path: string | null | undefined): string {
    return this.social.resolveImageUrl(path);
  }
}
